export const SALUD_CLINICA_V2_ENDPOINT = "/salud-clinica-v2/salud";
export const CATALOGO_CLINICO_V2_ENDPOINT = "/salud-clinica-v2/catalogo";
export const SALUD_CLINICA_V2_CREATE_PATH = "/saludFormularioV2";
export const SALUD_CLINICA_V2_EDIT_PATH = "/editarSaludV2";

const SALUD_V2_ENABLED_VALUES = new Set(["true", "1", "yes", "on"]);
const SALUD_V2_ACTIVITY_NAMES = new Set([
  "jornada medica",
  "salud general",
  "telemedicina",
]);

export const SALUD_V2_FIELD_LIMITS = {
  cedula: 45,
  telefono: 45,
  codigoBeneficiario: 55,
  nombreRepresentante: 45,
  apellidoRepresentante: 255,
  nombre: 45,
  apellido: 45,
  sexo: 45,
  genero: 255,
  lgbtqi: 45,
  rangoEdad: 45,
  ingresos: 45,
  cabezaFamilia: 45,
  peso: 45,
  estado: 45,
  municipio: 45,
  parroquia: 45,
  comunidad: 45,
  centro: 255,
  motivoConsulta1: 255,
  motivoConsulta2: 255,
  motivoConsulta3: 255,
  motivoConsulta4: 255,
  motivoConsulta5: 255,
  comentarios: 45,
};

const firstDefined = (...values) => {
  const value = values.find(
    (candidate) => candidate !== undefined && candidate !== null
  );
  return value === undefined ? values[values.length - 1] : value;
};

export const CREATION_ROLES_V2 = new Set(
  Array.from({ length: 12 }, (_, index) => index + 1)
);
export const EDIT_ROLES_V2 = new Set([1, 2, 10, 11]);

export const API_ERROR_MESSAGES_V2 = {
  AUTH_REQUIRED: "Debe iniciar sesión para continuar.",
  AUTH_INVALID: "La sesión no es válida. Inicie sesión nuevamente.",
  VALIDATION_ERROR: "Revise los datos clínicos antes de continuar.",
  UNAUTHORIZED: "La sesión expiró. Inicie sesión nuevamente.",
  FORBIDDEN: "No tiene permisos para realizar esta operación.",
  CATALOG_FORBIDDEN: "No tiene permisos para consultar el catálogo clínico.",
  CONCURRENT_UPDATE:
    "Este registro fue modificado por otra persona. Recargue la información antes de volver a editar.",
  CONVOCATORIA_NOT_ENABLED_FOR_TRANSCRIPTION:
    "La convocatoria no está habilitada para transcripción clínica V2.",
  DOCTOR_NOT_FOUND: "No se encontró el médico seleccionado.",
  DOCTOR_INACTIVE: "El médico seleccionado se encuentra inactivo.",
  DOCTOR_NOT_AUTHORIZED: "El médico seleccionado no está autorizado para esta atención.",
  MIXED_FLOW_NOT_READY: "La convocatoria todavía no está disponible para el flujo clínico V2.",
  UNSUPPORTED_CLINICAL_ACTIVITY: "La actividad no admite registros de Salud Clínica V2.",
  DIAGNOSIS_NOT_SELECTABLE: "Uno de los diagnósticos ya no está disponible en el catálogo.",
  CONTEXTUAL_RULE_NOT_READY: "No se pudo validar el contexto clínico de la convocatoria.",
  TREATMENT_NOT_FOUND: "Uno de los tratamientos seleccionados ya no está disponible.",
  HEALTH_RECORD_NOT_EDITABLE: "Este registro de salud no puede editarse mediante el flujo V2.",
};

export const safeJsonParse = (value, fallback = {}) => {
  if (!value) return fallback;

  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

export const getStoredUser = () =>
  safeJsonParse(window.localStorage.getItem("token"), {});

export const getNumericRole = (user) => Number(user?.rol);

export const canCreateSaludV2 = (user) =>
  CREATION_ROLES_V2.has(getNumericRole(user));

export const canEditSaludV2 = (user) => EDIT_ROLES_V2.has(getNumericRole(user));

export const isSaludClinicaV2UiEnabled = (
  value = process.env.REACT_APP_SALUD_CLINICA_V2_UI_ENABLED
) => SALUD_V2_ENABLED_VALUES.has(String(value || "").trim().toLowerCase());

const normalizeActivityNameV2 = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

export const getSaludV2Activities = (convocatoria = {}) => {
  const activities = convocatoria.actividad ?? convocatoria.actividades ?? [];
  return (Array.isArray(activities) ? activities : [activities])
    .map(normalizeActivityNameV2)
    .filter(Boolean);
};

export const isSaludV2ClinicalFlow = (convocatoria = {}) => {
  const activities = getSaludV2Activities(convocatoria);
  return (
    activities.length > 0 &&
    activities.every((activity) => SALUD_V2_ACTIVITY_NAMES.has(activity))
  );
};

export const isSaludV2TranscriptionState = (convocatoria = {}) => {
  const knownState =
    convocatoria.estadoConvocatoria ??
    convocatoria.estado_convocatoria ??
    convocatoria.idEstadoConvocatoria ??
    convocatoria.id_estado_convocatoria;

  return knownState === undefined || knownState === null || Number(knownState) === 2;
};

export const canAccessSaludV2Creation = ({
  enabled = isSaludClinicaV2UiEnabled(),
  user,
  convocatoria,
} = {}) =>
  Boolean(enabled) &&
  canCreateSaludV2(user) &&
  isSaludV2ClinicalFlow(convocatoria) &&
  isSaludV2TranscriptionState(convocatoria);

export const canAccessSaludV2Route = ({
  enabled = isSaludClinicaV2UiEnabled(),
  user,
  mode = "create",
} = {}) =>
  Boolean(enabled) &&
  (mode === "edit" ? canEditSaludV2(user) : canCreateSaludV2(user));

export const buildSaludV2CreationPath = (convocatoria = {}) =>
  `${SALUD_CLINICA_V2_CREATE_PATH}?prop=${encodeURIComponent(
    JSON.stringify(convocatoria)
  )}`;

export const isTelemedicinaSaludV2 = (convocatoria = {}) =>
  getSaludV2Activities(convocatoria).includes("telemedicina");

export const getCreatedSaludV2Id = (payload) => payload?.data?.id ?? null;

export const createEmptyDiagnosisV2 = () => ({
  idConcepto: null,
  nombre: "",
  codigo: "",
  sistema: "",
  textoOriginal: "",
  pendienteRevision: false,
  agrupacionPatologia: null,
});

const normalizePathologyGroupV2 = (group) => {
  if (!group || typeof group !== "object") return null;

  const idPatologia = group.idPatologia;
  const descripcion = String(group.descripcion || "").trim();
  const numericId = Number(idPatologia);

  if (!Number.isInteger(numericId) || numericId <= 0 || !descripcion) {
    return null;
  }

  return { idPatologia, descripcion };
};

export const deriveDisplayedPathologyGroups = (diagnoses = []) => {
  if (!Array.isArray(diagnoses)) return [];

  const seenIds = new Set();
  const displayedGroups = [];

  for (const diagnosis of diagnoses) {
    const group = normalizePathologyGroupV2(
      diagnosis?.agrupacionPatologia
    );

    if (!group) continue;

    const groupId = String(Number(group.idPatologia));
    if (seenIds.has(groupId)) continue;

    seenIds.add(groupId);
    displayedGroups.push(group);

    if (displayedGroups.length === 2) break;
  }

  return displayedGroups;
};

export const normalizeCatalogResponseV2 = (payload) => {
  const candidates =
    payload?.data?.items ??
    payload?.data?.resultados ??
    payload?.data ??
    payload?.items ??
    payload?.resultados ??
    payload;

  if (!Array.isArray(candidates)) return [];

  return candidates
    .map((item) => ({
      idConcepto: item?.idConcepto ?? item?.id_concepto ?? item?.id ?? null,
      nombre:
        item?.nombre ??
        item?.nombreConcepto ??
        item?.nombre_concepto ??
        item?.descripcion ??
        "",
      codigo:
        item?.codigo ?? item?.codigoClinico ?? item?.codigo_clinico ?? item?.codigo_cie10 ?? "",
      sistema:
        item?.sistemaCodificacion ??
        item?.sistema_codificacion ??
        item?.sistema ??
        item?.sistemaCodigo ??
        item?.sistema_codigo ??
        "",
      tipoConcepto: item?.tipoConcepto ?? item?.tipo_concepto ?? "",
      requiereContexto: Boolean(
        item?.requiereContexto ?? item?.requiere_contexto
      ),
      codigoReglaContextual:
        item?.codigoReglaContextual ?? item?.codigo_regla_contextual ?? null,
      estado: item?.estado ?? "",
      seleccionable: item?.seleccionable !== false,
      coincidencia: item?.coincidencia ?? null,
      agrupacionPatologia: normalizePathologyGroupV2(
        item?.agrupacionPatologia
      ),
    }))
    .filter((item) => item.idConcepto !== null && item.nombre);
};

export const normalizeDiagnosisV2 = (diagnosis = {}) => ({
  idConcepto: diagnosis.idConcepto ?? diagnosis.id_concepto ?? null,
  nombre:
    diagnosis.nombre ??
    diagnosis.nombreConcepto ??
    diagnosis.nombre_concepto ??
    diagnosis.textoOriginal ??
    diagnosis.texto_original ??
    "",
  codigo: diagnosis.codigo ?? diagnosis.codigoClinico ?? diagnosis.codigo_clinico ?? "",
  sistema:
    diagnosis.sistema ??
    diagnosis.sistemaCodificacion ??
    diagnosis.sistemaCodigo ??
    diagnosis.sistema_codigo ??
    "",
  textoOriginal: diagnosis.textoOriginal ?? diagnosis.texto_original ?? "",
  pendienteRevision: Boolean(
    diagnosis.pendienteRevision ?? diagnosis.pendiente_revision
  ),
  agrupacionPatologia: normalizePathologyGroupV2(
    diagnosis.agrupacionPatologia
  ),
});

export const validateDiagnosesV2 = (diagnoses = []) => {
  if (!Array.isArray(diagnoses) || diagnoses.length < 1) {
    return "Debe registrar al menos un diagnóstico.";
  }

  if (diagnoses.length > 5) {
    return "Solo puede registrar hasta cinco diagnósticos.";
  }

  const incomplete = diagnoses.some(
    (diagnosis) =>
      diagnosis?.idConcepto === null &&
      !(
        diagnosis?.pendienteRevision &&
        String(diagnosis?.textoOriginal || "").trim()
      )
  );

  if (incomplete) {
    return "Seleccione un concepto o registre el texto para revisión.";
  }

  const canonicalIds = diagnoses
    .map((diagnosis) => diagnosis?.idConcepto)
    .filter((id) => id !== null && id !== undefined)
    .map(String);

  if (new Set(canonicalIds).size !== canonicalIds.length) {
    return "No puede registrar dos veces el mismo concepto clínico.";
  }

  return "";
};

export const serializeDiagnosesV2 = (diagnoses = []) =>
  diagnoses.map((diagnosis, index) => {
    const serialized = {
      idConcepto: diagnosis.idConcepto ?? null,
      orden: index + 1,
      esPrincipal: index === 0,
      textoOriginal: String(
        diagnosis.textoOriginal || diagnosis.nombre || ""
      ).trim(),
    };

    if (diagnosis.pendienteRevision) {
      serialized.pendienteRevision = true;
    }

    return serialized;
  });

export const getMedicationIdV2 = (medication) =>
  medication?.medicamentoId ??
  medication?.idMedicamento ??
  medication?.id_medicamento ??
  medication?.medicamento?.id ??
  medication?.medicamento?.id_medicamento ??
  medication?.id ??
  null;

export const normalizeMedicationV2 = (medication = {}) => ({
  ...medication,
  medicamentoId: getMedicationIdV2(medication),
  descripcion:
    medication.descripcion ??
    medication.nombre ??
    medication.label ??
    medication.medicamento?.descripcion ??
    medication.medicamento?.nombre ??
    "Medicamento",
});

export const serializeTreatmentsV2 = (treatments = []) =>
  treatments
    .map((treatment) => getMedicationIdV2(treatment))
    .filter((medicamentoId) => medicamentoId !== null && medicamentoId !== undefined)
    .map((medicamentoId) => ({ medicamentoId }));

export const buildCatalogSearchParamsV2 = (query) => ({
  q: String(query || "").trim(),
  limite: 20,
});

export const buildSaludV2Payload = (salud = {}) => ({
  cedula: firstDefined(salud.cedula, ""),
  telefono: firstDefined(salud.telefono, ""),
  codigoBeneficiario: firstDefined(
    salud.codigoBeneficiario,
    salud.codigo_beneficiario,
    salud.codigo,
    ""
  ),
  nombreRepresentante: firstDefined(
    salud.nombreRepresentante,
    salud.nombre_representante,
    salud.nombre_rep,
    ""
  ),
  apellidoRepresentante: firstDefined(
    salud.apellidoRepresentante,
    salud.apellido_representante,
    salud.apellido_rep,
    ""
  ),
  nombre: firstDefined(salud.nombre, ""),
  apellido: firstDefined(salud.apellido, ""),
  sexo: firstDefined(salud.sexo, ""),
  genero: firstDefined(salud.genero, ""),
  edad: firstDefined(salud.edad, ""),
  lgbtqi: firstDefined(salud.lgbtqi, ""),
  grupoEtnicoId: firstDefined(
    salud.grupoEtnicoId,
    salud.grupo_etnico_id,
    salud.grupo_etnico,
    ""
  ),
  fechaNacimiento: firstDefined(
    salud.fechaNacimiento,
    salud.fecha_nacimiento,
    null
  ),
  rangoEdad: firstDefined(salud.rangoEdad, salud.rango_edad, ""),
  nivelEducativoId: firstDefined(
    salud.nivelEducativoId,
    salud.nivel_educativo_id,
    salud.nivel_educativo,
    ""
  ),
  ingresos: firstDefined(salud.ingresos, ""),
  discapacidadId: firstDefined(
    salud.discapacidadId,
    salud.discapacidad_id,
    salud.discapacidad,
    ""
  ),
  movilidadId: firstDefined(
    salud.movilidadId,
    salud.movilidad_id,
    salud.movilidad,
    ""
  ),
  factorRiesgo1Id: firstDefined(
    salud.factorRiesgo1Id,
    salud.factor_riesgo1_id,
    salud.factores_riesgo1,
    ""
  ),
  factorRiesgo2Id: firstDefined(
    salud.factorRiesgo2Id,
    salud.factor_riesgo2_id,
    salud.factores_riesgo2,
    ""
  ),
  factorRiesgo3Id: firstDefined(
    salud.factorRiesgo3Id,
    salud.factor_riesgo3_id,
    salud.factores_riesgo3,
    ""
  ),
  cabezaFamilia: firstDefined(
    salud.cabezaFamilia,
    salud.cabeza_familia,
    ""
  ),
  peso: firstDefined(salud.peso, ""),
  estado: firstDefined(salud.estado, ""),
  municipio: firstDefined(salud.municipio, ""),
  parroquia: firstDefined(salud.parroquia, ""),
  comunidad: firstDefined(salud.comunidad, ""),
  centro: firstDefined(salud.centro, ""),
  fechaEvaluacion: firstDefined(
    salud.fechaEvaluacion,
    salud.fecha_evaluacion,
    null
  ),
  motivoConsulta1: firstDefined(salud.motivoConsulta1, null),
  motivoConsulta2: firstDefined(salud.motivoConsulta2, null),
  motivoConsulta3: firstDefined(salud.motivoConsulta3, null),
  motivoConsulta4: firstDefined(salud.motivoConsulta4, null),
  motivoConsulta5: firstDefined(salud.motivoConsulta5, null),
  comentarios: firstDefined(salud.comentarios, ""),
});

export const validateSaludV2FieldLimits = (salud = {}) =>
  Object.entries(SALUD_V2_FIELD_LIMITS)
    .filter(([field, maxLength]) =>
      salud[field] !== null &&
      salud[field] !== undefined &&
      String(salud[field]).length > maxLength
    )
    .map(([field, maxLength]) => ({
      field,
      maxLength,
      actualLength: String(salud[field]).length,
      message: `${field} admite un maximo de ${maxLength} caracteres.`,
    }));

export const buildSaludV2Request = ({
  convocatoriaId,
  medicoId,
  expectedVersion,
  salud,
  diagnosticos = [],
  tratamientos = [],
}) => {
  const normalizeNumericValue = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : value;
  };

  const request = {
    convocatoriaId: normalizeNumericValue(convocatoriaId),
    medicoId: normalizeNumericValue(medicoId),
    salud: buildSaludV2Payload(salud),
    diagnosticos: serializeDiagnosesV2(diagnosticos),
    tratamientos: serializeTreatmentsV2(tratamientos),
  };

  if (expectedVersion !== undefined) {
    request.expectedVersion = normalizeNumericValue(expectedVersion);
  }

  return request;
};

export const extractApiErrorV2 = (error) => {
  const apiError = error?.response?.data?.error;
  const code = apiError?.code || "UNKNOWN_ERROR";

  return {
    code,
    status: error?.response?.status,
    message:
      API_ERROR_MESSAGES_V2[code] ||
      apiError?.message ||
      "No se pudo completar la operación. Intente nuevamente.",
  };
};

export const unwrapHealthRecordV2 = (payload) => {
  const envelope = payload?.data ?? payload ?? {};
  const recordEnvelope = envelope?.registro ?? envelope;
  const salud = recordEnvelope?.salud ?? recordEnvelope;

  return {
    ...salud,
    diagnosticos: recordEnvelope?.diagnosticos ?? salud?.diagnosticos ?? [],
    tratamientos: recordEnvelope?.tratamientos ?? salud?.tratamientos ?? [],
    version: recordEnvelope?.version ?? salud?.version,
    source: recordEnvelope?.source ?? recordEnvelope?.origen ?? salud?.source ?? salud?.origen,
    editableInV2:
      recordEnvelope?.editableInV2 ??
      recordEnvelope?.editable_en_v2 ??
      salud?.editableInV2 ??
      salud?.editable_en_v2,
    convocatoriaId:
      recordEnvelope?.convocatoriaId ??
      recordEnvelope?.idConvocatoria ??
      recordEnvelope?.id_convocatoria ??
      recordEnvelope?.convocatoria?.id ??
      salud?.convocatoriaId ??
      salud?.idConvocatoria ??
      salud?.id_convocatoria,
    medicoId:
      recordEnvelope?.medicoId ??
      recordEnvelope?.medico_id ??
      recordEnvelope?.medico?.id ??
      salud?.medicoId ??
      salud?.medico_id,
    medicoNombre:
      recordEnvelope?.medicoNombre ??
      recordEnvelope?.medico_nombre ??
      recordEnvelope?.medico?.nombreCompleto ??
      salud?.medicoNombre ??
      salud?.medico_nombre,
    codigoProyecto:
      recordEnvelope?.codigoProyecto ??
      recordEnvelope?.codigo_proyecto ??
      recordEnvelope?.proyecto?.codigo ??
      salud?.codigoProyecto ??
      salud?.codigo_proyecto,
  };
};

export const normalizeSaludV2Response = (payload) => {
  const record = unwrapHealthRecordV2(payload);

  return {
    id: firstDefined(record.id, record.saludId, record.salud_id, null),
    convocatoriaId: firstDefined(
      record.convocatoriaId,
      record.idConvocatoria,
      record.id_convocatoria,
      record.convocatoria?.id,
      null
    ),
    medicoId: firstDefined(
      record.medicoId,
      record.medico_id,
      record.medico?.id,
      null
    ),
    medicoNombre: firstDefined(
      record.medicoNombre,
      record.medico_nombre,
      record.medico?.nombreCompleto,
      ""
    ),
    codigoProyecto: firstDefined(
      record.codigoProyecto,
      record.codigo_proyecto,
      record.proyecto?.codigo,
      ""
    ),
    version: firstDefined(record.version, null),
    source: firstDefined(record.source, record.origen, ""),
    editableInV2: firstDefined(
      record.editableInV2,
      record.editable_en_v2,
      true
    ),
    cedula: firstDefined(record.cedula, ""),
    telefono: firstDefined(record.telefono, ""),
    codigo: firstDefined(
      record.codigoBeneficiario,
      record.codigo_beneficiario,
      record.codigo,
      ""
    ),
    nombre_rep: firstDefined(
      record.nombreRepresentante,
      record.nombre_representante,
      record.nombre_rep,
      ""
    ),
    apellido_rep: firstDefined(
      record.apellidoRepresentante,
      record.apellido_representante,
      record.apellido_rep,
      ""
    ),
    nombre: firstDefined(record.nombre, ""),
    apellido: firstDefined(record.apellido, ""),
    sexo: firstDefined(record.sexo, ""),
    genero: firstDefined(record.genero, ""),
    edad: firstDefined(record.edad, ""),
    lgbtqi: firstDefined(record.lgbtqi, ""),
    grupo_etnico: firstDefined(
      record.grupoEtnicoId,
      record.grupo_etnico_id,
      record.grupo_etnico,
      ""
    ),
    fecha_nacimiento: firstDefined(
      record.fechaNacimiento,
      record.fecha_nacimiento,
      null
    ),
    rango_edad: firstDefined(record.rangoEdad, record.rango_edad, ""),
    nivel_educativo: firstDefined(
      record.nivelEducativoId,
      record.nivel_educativo_id,
      record.nivel_educativo,
      ""
    ),
    ingresos: firstDefined(record.ingresos, ""),
    discapacidad: firstDefined(
      record.discapacidadId,
      record.discapacidad_id,
      record.discapacidad,
      ""
    ),
    movilidad: firstDefined(
      record.movilidadId,
      record.movilidad_id,
      record.movilidad,
      ""
    ),
    factores_riesgo1: firstDefined(
      record.factorRiesgo1Id,
      record.factor_riesgo1_id,
      record.factores_riesgo1,
      ""
    ),
    factores_riesgo2: firstDefined(
      record.factorRiesgo2Id,
      record.factor_riesgo2_id,
      record.factores_riesgo2,
      ""
    ),
    factores_riesgo3: firstDefined(
      record.factorRiesgo3Id,
      record.factor_riesgo3_id,
      record.factores_riesgo3,
      ""
    ),
    cabeza_familia: firstDefined(
      record.cabezaFamilia,
      record.cabeza_familia,
      ""
    ),
    peso: firstDefined(record.peso, ""),
    estado: firstDefined(record.estado, ""),
    municipio: firstDefined(record.municipio, ""),
    parroquia: firstDefined(record.parroquia, ""),
    comunidad: firstDefined(record.comunidad, ""),
    centro: firstDefined(record.centro, ""),
    fecha_evaluacion: firstDefined(
      record.fechaEvaluacion,
      record.fecha_evaluacion,
      null
    ),
    motivoConsulta1: firstDefined(record.motivoConsulta1, record.motivo_consulta1, null),
    motivoConsulta2: firstDefined(record.motivoConsulta2, record.motivo_consulta2, null),
    motivoConsulta3: firstDefined(record.motivoConsulta3, record.motivo_consulta3, null),
    motivoConsulta4: firstDefined(record.motivoConsulta4, record.motivo_consulta4, null),
    motivoConsulta5: firstDefined(record.motivoConsulta5, record.motivo_consulta5, null),
    comentarios: firstDefined(record.comentarios, ""),
    diagnosticos: Array.isArray(record.diagnosticos) ? record.diagnosticos : [],
    tratamientos: Array.isArray(record.tratamientos) ? record.tratamientos : [],
  };
};
